import React from "react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Copy, Lightbulb, Target, Users, ArrowRight, RefreshCw } from "lucide-react";
import { copyToClipboard } from "../../utils/clipboard";
import RegenerateSection from "./RegenerateSection";

function BlogIdeasTab({ content, onRegenerate, isRegenerating, onRegenerateItem, regeneratingItem }) {
  if (!content || content.length === 0) {
    return (
      <div className="text-center py-12">
        <Lightbulb className="w-12 h-12 text-white/20 mx-auto mb-4" />
        <p className="text-white/60 mb-4">No blog ideas generated yet.</p>
        {onRegenerate && <RegenerateSection onRegenerate={onRegenerate} isRegenerating={isRegenerating} contentType="Blog Ideas" />}
      </div>
    );
  }

  const copyIdea = (idea) => {
    const text = `Title: ${idea.title}\nAngle: ${idea.angle}\nAudience: ${idea.audience}\nConnection: ${idea.connection}`;
    copyToClipboard(text);
  };

  const copyAllIdeas = () => {
    const allText = content.map((idea, idx) => 
      `${idx + 1}. ${idea.title}\nAngle: ${idea.angle}\nAudience: ${idea.audience}\nConnection: ${idea.connection}`
    ).join('\n\n');
    copyToClipboard(allText);
  };

  return (
    <div className="max-w-5xl mx-auto space-y-6 animate-fadeIn">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center p-3 bg-purple-500/20 rounded-full mb-4">
          <Lightbulb className="w-8 h-8 text-purple-400" />
        </div>
        <h2 className="text-2xl font-bold text-white mb-2">
          Blog Topic Ideas
        </h2>
        <p className="text-white/60 max-w-2xl mx-auto">
          5 complementary topics to expand your content strategy
        </p>
      </div>

      {/* Copy All Button */}
      <div className="flex justify-end">
        <Button
          onClick={copyAllIdeas}
          variant="outline"
          size="sm"
        >
          <Copy className="w-4 h-4 mr-2" />
          Copy All Ideas
        </Button>
      </div>

      {/* Ideas Grid */}
      <div className="space-y-4">
        {content.map((idea, index) => (
          <Card key={index} className="card hover:border-purple-500/30 transition-all">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                {/* Number Badge */}
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center">
                  <span className="text-purple-400 font-bold">{index + 1}</span>
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0 space-y-4">
                  {/* Title */}
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-1">
                      {idea.title}
                    </h3>
                  </div>

                  {/* Angle */}
                  <div className="flex items-start gap-3">
                    <ArrowRight className="w-5 h-5 text-[#00d4aa] flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-white/70 mb-1">Angle</p>
                      <p className="text-white/90">{idea.angle}</p>
                    </div>
                  </div>

                  {/* Audience */}
                  <div className="flex items-start gap-3">
                    <Users className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-white/70 mb-1">Target Audience</p>
                      <p className="text-white/90">{idea.audience}</p>
                    </div>
                  </div>

                  {/* Connection */}
                  <div className="flex items-start gap-3">
                    <Target className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-white/70 mb-1">Connection to Source</p>
                      <p className="text-white/90">{idea.connection}</p>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col items-center gap-1 flex-shrink-0">
                  {onRegenerateItem && (
                    <button
                      onClick={() => onRegenerateItem(index)}
                      disabled={regeneratingItem === index}
                      className="p-1.5 rounded hover:bg-white/10 text-white/40 hover:text-white transition-colors"
                      title="Regenerate this idea"
                    >
                      <RefreshCw size={14} className={regeneratingItem === index ? 'animate-spin' : ''} />
                    </button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyIdea(idea)}
                    className="flex-shrink-0"
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Footer Tip */}
      <div className="p-4 bg-purple-500/10 border border-purple-500/30 rounded-lg text-center">
        <p className="text-white/80 text-sm">
          <strong className="text-purple-400">Tip:</strong> Use these topics to create a content series or cluster around your original post for better SEO.
        </p>
      </div>

      {/* Regenerate Section */}
      {onRegenerate && (
        <RegenerateSection
          onRegenerate={onRegenerate}
          isRegenerating={isRegenerating}
          contentType="Blog Ideas"
        />
      )}
    </div>
  );
}

export default BlogIdeasTab;
